import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { SessionsState, SessionState } from '../types';
import { apiSlice } from '../api/apiSlice';
import { createDiagnosticSession } from '../thunks/sessionThunks';

const initialState: SessionsState = {
  current: null,
  history: [],
};

const sessionsSlice = createSlice({
  name: 'sessions',
  initialState,
  reducers: {
    // Set the current active session
    setCurrentSession: (state, action: PayloadAction<SessionState>) => {
      console.log('📝 Setting current session:', action.payload.sessionId);
      state.current = action.payload;
    },
    
    // Clear current session and move to history
    clearCurrentSession: (state) => {
      console.log('🧹 Clearing current session:', state.current?.sessionId);
      if (state.current) {
        // Move current session to history with end time
        const endedSession = {
          ...state.current,
          endTime: new Date().toISOString(),
          isActive: false,
        };
        
        // Add to history (avoid duplicates)
        const existingIndex = state.history.findIndex(
          session => session.sessionId === endedSession.sessionId
        );
        
        if (existingIndex >= 0) {
          state.history[existingIndex] = endedSession;
        } else {
          state.history.unshift(endedSession);
        }
        
        // Keep only last 50 sessions in history
        state.history = state.history.slice(0, 50);
      }
      state.current = null;
    },
    
    // Update current session with partial data
    updateCurrentSession: (state, action: PayloadAction<Partial<SessionState>>) => {
      if (state.current) {
        console.log('📝 Updating current session:', action.payload);
        state.current = { ...state.current, ...action.payload };
      }
    },
    
    // Increment questions asked counter
    incrementQuestionsAsked: (state) => {
      if (state.current) {
        state.current.questionsAsked += 1;
        console.log('📈 Questions asked incremented to:', state.current.questionsAsked);
      }
    },
    
    // Clear all session history
    clearSessionHistory: (state) => {
      console.log('🗑️ Clearing session history');
      state.history = [];
    },
    
    // Enhanced session creation with validation
    createSession: (state, action: PayloadAction<{
      sessionId: string;
      fieldId: string;
      caseId: string;
    }>) => {
      const { sessionId, fieldId, caseId } = action.payload;
      
      console.log('🆕 Creating new session in state:', { sessionId, fieldId, caseId });
      
      // Clear previous session first
      if (state.current) {
        console.log('🧹 Moving previous session to history:', state.current.sessionId);
        const endedSession = {
          ...state.current,
          endTime: new Date().toISOString(),
          isActive: false,
        };
        
        // Avoid duplicates in history
        const existingIndex = state.history.findIndex(
          session => session.sessionId === endedSession.sessionId
        );
        
        if (existingIndex >= 0) {
          state.history[existingIndex] = endedSession;
        } else {
          state.history.unshift(endedSession);
        }
      }
      
      // Create new session (simplified - no categoriesCovered)
      state.current = {
        sessionId,
        fieldId,
        caseId,
        startTime: new Date().toISOString(),
        questionsAsked: 0,
        isActive: true,
      };
      
      console.log('✅ New session created in state:', state.current);
    },
    
    // Reset everything (for emergency cleanup)
    resetSessions: (state) => {
      console.log('🔄 Resetting all session state');
      return { ...initialState };
    },
    
    // Mark session as inactive (for cleanup)
    markSessionInactive: (state) => {
      if (state.current) {
        console.log('⏸️ Marking session as inactive:', state.current.sessionId);
        state.current.isActive = false;
        state.current.endTime = new Date().toISOString();
      }
    },
    
    // Update session duration (for analytics)
    updateSessionDuration: (state) => {
      if (state.current && state.current.startTime) {
        // Duration is calculated dynamically in selectors, but we can track end time
        if (!state.current.endTime) {
          state.current.endTime = new Date().toISOString();
        }
      }
    },
    
    // Set session error state (for error handling)
    setSessionError: (state, action: PayloadAction<string>) => {
      if (state.current) {
        console.log('❌ Setting session error:', action.payload);
        // You could add an error field to SessionState if needed
        // For now, we'll mark as inactive
        state.current.isActive = false;
      }
    },
  },
  
  extraReducers: (builder) => {
    // Handle the createDiagnosticSession thunk
    builder
      .addCase(createDiagnosticSession.pending, (state) => {
        console.log('🔄 Session creation pending...');
      })
      .addCase(createDiagnosticSession.fulfilled, (state) => {
        console.log('✅ Session creation fulfilled');
        // Session should already be set by the createSession action
      })
      .addCase(createDiagnosticSession.rejected, (state, action) => {
        console.error('❌ Session creation rejected:', action.payload);
        
        // Provide more detailed error info
        const error = action.payload || action.error?.message || 'Unknown error';
        console.error('❌ Full error details:', {
          payload: action.payload,
          error: action.error,
          meta: action.meta
        });
        
        // Clean up any partial state
        state.current = null;
      });
  
    // Keep RTK Query handlers for compatibility but with better error handling
    builder
      .addMatcher(apiSlice.endpoints.createSession.matchPending, (state) => {
        console.log('📡 RTK Query session creation pending...');
      })
      .addMatcher(apiSlice.endpoints.createSession.matchFulfilled, (state, action) => {
        const { sessionId } = action.payload;
        const { fieldId, caseId } = action.meta.arg.originalArgs;
        
        console.log('📡 RTK Query session creation fulfilled:', { sessionId, fieldId, caseId });
        
        // This might be redundant with the manual createSession action,
        // but keeping for safety
        if (!state.current || state.current.sessionId !== sessionId) {
          console.log('📡 RTK Query creating session state (manual creation may have failed)');
          
          // Clear previous session
          if (state.current) {
            state.history.unshift({
              ...state.current,
              endTime: new Date().toISOString(),
              isActive: false,
            });
          }
          
          // Create new session (simplified)
          state.current = {
            sessionId,
            fieldId,
            caseId,
            startTime: new Date().toISOString(),
            questionsAsked: 0,
            // REMOVED: categoriesCovered: [] - no longer tracking categories
            isActive: true,
          };
        }
      })
      .addMatcher(apiSlice.endpoints.createSession.matchRejected, (state, action) => {
        console.error('❌ RTK Query session creation failed:', action.error);
        console.error('❌ RTK Query full error:', {
          error: action.error,
          meta: action.meta,
          originalArgs: action.meta?.arg?.originalArgs
        });
        
        state.current = null;
      })
      .addMatcher(apiSlice.endpoints.endSession.matchFulfilled, (state) => {
        console.log('📡 RTK Query session ended');
        if (state.current) {
          state.history.unshift({
            ...state.current,
            endTime: new Date().toISOString(),
            isActive: false,
          });
          state.current = null;
        }
      });
  },
});

export const {
  setCurrentSession,
  clearCurrentSession,
  updateCurrentSession,
  incrementQuestionsAsked,
  clearSessionHistory,
  createSession,
  resetSessions,
  markSessionInactive,
  updateSessionDuration,
  setSessionError,
} = sessionsSlice.actions;

export default sessionsSlice.reducer;